/**
 * Mappt eine Online-Anmeldung auf das Prefill-Format von NewEnrollmentDialog.
 */
import { vokalSpelling } from '@/lib/utils';
import { composeStreetLine } from '@/lib/addressLine';

function joinAddress(person) {
  return composeStreetLine(person);
}

function normalizeTime(value) {
  if (value == null || value === '') return '';
  const m = String(value).trim().match(/^(\d{1,2}):(\d{2})/);
  if (!m) return '';
  return `${m[1].padStart(2, '0')}:${m[2]}`;
}

function weekdayFromIsoDate(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  return String(d.getDay()); // 0=So … 6=Sa — wie NewEnrollmentDialog DAYS
}

function normalizeMatchText(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/unterricht/g, '')
    .replace(/[.\-_/]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const FORM_KEYWORDS_RE = /\s+(Einzel|Gruppe|Karte|Abo|Ensemble|Kombi|GEMA|Aufschlag|\d+(?:-\d+)?er)\b/i;

export function parseSubjectName(name) {
  const m = FORM_KEYWORDS_RE.exec(name || '');
  if (m) {
    return {
      instrument: name.slice(0, m.index).trim() || name.trim(),
      form: name.slice(m.index).trim(),
    };
  }
  return { instrument: String(name || '').trim(), form: '' };
}

/**
 * Löst Fach + Unterrichtsform aus Text-Hinweisen der Online-Anmeldung
 * auf Stammdaten-IDs (nach dem Laden von Subjects/Tarifen).
 */
export function resolveEnrollmentSubjectPrefill({
  subjects = [],
  tariffs = [],
  instrumentHint = '',
  teachingTypeHint = '',
  durationMinutes = null,
} = {}) {
  const instrHint = normalizeMatchText(instrumentHint);
  const formHint = normalizeMatchText(teachingTypeHint);
  const durHint = durationMinutes != null
    ? String(durationMinutes)
    : (String(teachingTypeHint || '').match(/(\d{2,3})\s*Min/i)?.[1] || null);

  if (!subjects.length || (!instrHint && !formHint)) {
    return { subject_id: '', tariff_id: '' };
  }

  const parsed = subjects.map((s) => ({ id: s.id, name: s.name || '', ...parseSubjectName(s.name) }));
  const combinedCount = parsed.filter((p) => p.form).length;
  const combinedMode = combinedCount / parsed.length > 0.3;

  const scoreInstrument = (name) => {
    const n = normalizeMatchText(name);
    if (!instrHint || !n) return 0;
    if (n === instrHint) return 100;
    if (n.startsWith(instrHint) || instrHint.startsWith(n)) return 80;
    if (n.includes(instrHint) || instrHint.includes(n)) return 60;
    return 0;
  };

  const scoreForm = (form) => {
    const f = normalizeMatchText(form);
    if (!f) return 0;
    let s = 0;
    if (formHint && (f === formHint || f.includes(formHint) || formHint.includes(f))) s += 40;
    if (/einzel/i.test(formHint) && /einzel/i.test(f)) s += 25;
    if (/gruppe/i.test(formHint) && /gruppe/i.test(f)) s += 25;
    if (/karte/i.test(formHint) && /karte/i.test(f)) s += 25;
    if (durHint && new RegExp(`${durHint}\\s*min`, 'i').test(form)) s += 35;
    if (!formHint && /einzel/i.test(f)) s += 10;
    if (!formHint && durHint && new RegExp(`${durHint}\\s*min`, 'i').test(form)) s += 20;
    return s;
  };

  if (combinedMode) {
    const byInstrument = {};
    for (const p of parsed) {
      if (!p.instrument || /^\d+er/i.test(p.instrument)) continue;
      if (!byInstrument[p.instrument]) byInstrument[p.instrument] = [];
      byInstrument[p.instrument].push(p);
    }
    let bestInstr = null;
    let bestInstrScore = 0;
    for (const instr of Object.keys(byInstrument)) {
      const sc = scoreInstrument(instr);
      if (sc > bestInstrScore) {
        bestInstrScore = sc;
        bestInstr = instr;
      }
    }
    if (!bestInstr || bestInstrScore < 40) {
      return { subject_id: '', tariff_id: '' };
    }
    const forms = byInstrument[bestInstr];
    let bestForm = forms[0];
    let bestFormScore = -1;
    for (const f of forms) {
      const sc = scoreForm(f.form || f.name);
      if (sc > bestFormScore) {
        bestFormScore = sc;
        bestForm = f;
      }
    }
    return {
      subject_id: `__combined__${bestInstr}`,
      tariff_id: bestForm?.id || '',
    };
  }

  let bestSubject = null;
  let bestSubjectScore = 0;
  for (const p of parsed) {
    const sc = scoreInstrument(p.name) || scoreInstrument(p.instrument);
    if (sc > bestSubjectScore) {
      bestSubjectScore = sc;
      bestSubject = p;
    }
  }
  let bestTariffId = '';
  let bestTariffScore = -1;
  for (const t of tariffs) {
    const name = t?.raw?.name || t?.label || t?.name || '';
    const sc = scoreForm(name);
    if (sc > bestTariffScore) {
      bestTariffScore = sc;
      bestTariffId = t.value || t.id || '';
    }
  }
  return {
    subject_id: bestSubjectScore >= 40 ? (bestSubject?.id || '') : '',
    tariff_id: bestTariffScore >= 20 ? bestTariffId : '',
  };
}

/**
 * @param {object} detail — RegistrationDetail aus /admin/registrations/{id}
 * @returns {object} prefill für NewEnrollmentDialog
 */
export function registrationToEnrollmentPrefill(detail) {
  if (!detail) return null;
  const payload = detail.payload || {};
  const student = payload.student || {};
  const payer = payload.payer || {};
  const sepa = payload.sepa || {};
  const slot = detail.final_slot || payload.preferred_slot || payload.group?.proposed_slot || null;

  const studentAddressSame = !!student.address_same;
  const studentAddress = studentAddressSame ? joinAddress(payer) : joinAddress(student);
  const studentPostal = studentAddressSame ? (payer.postal_code || '') : (student.postal_code || '');
  const studentCity = studentAddressSame ? (payer.city || '') : (student.city || '');

  const instrumentHint = detail.instrument || student.instrument || payload.course_offer?.subject || '';
  const teachingTypeHint = payload.teaching_type
    || student.lesson_type
    || payload.course_offer?.title
    || '';

  const notesParts = [];
  if (instrumentHint) notesParts.push(`Instrument: ${instrumentHint}`);
  if (detail.category) notesParts.push(`Kategorie: ${vokalSpelling(detail.category)}`);
  if (teachingTypeHint) notesParts.push(`Unterrichtsart: ${teachingTypeHint}`);
  if (student.prior_knowledge) notesParts.push(`Vorkenntnisse: ${student.prior_knowledge}`);
  if (student.notes) notesParts.push(student.notes);
  notesParts.push(`Online-Anmeldung ${detail.id}`);

  const startTime = normalizeTime(slot?.start_time);
  const duration = slot?.duration_minutes != null ? String(slot.duration_minutes) : '45';
  const dayOfWeek = weekdayFromIsoDate(slot?.date);
  const courseNameBits = [
    instrumentHint || teachingTypeHint || 'Unterricht',
    [detail.student_first_name, detail.student_last_name].filter(Boolean).join(' '),
  ].filter(Boolean);

  return {
    registrationId: detail.id,
    courseMode: 'new',
    subjectHint: instrumentHint,
    teachingTypeHint,
    durationHint: slot?.duration_minutes ?? null,
    student: {
      first_name: detail.student_first_name || student.first_name || '',
      last_name: detail.student_last_name || student.last_name || '',
      date_of_birth: (student.birthdate || '').slice(0, 10),
      email: student.email || '',
      phone: student.phone || '',
      address: studentAddress,
      postal_code: studentPostal,
      city: studentCity,
      notes: notesParts.join('\n'),
    },
    payer: {
      first_name: detail.payer_first_name || payer.first_name || '',
      last_name: detail.payer_last_name || payer.last_name || '',
      email: detail.payer_email || payer.email || '',
      phone: payer.phone || '',
      address: joinAddress(payer),
      postal_code: payer.postal_code || '',
      city: payer.city || '',
    },
    bank: {
      account_holder: sepa.account_holder || '',
      iban: sepa.iban || '',
      bic: sepa.bic || '',
    },
    enrollment: {
      start_date: (slot?.date || new Date().toISOString().slice(0, 10)).slice(0, 10),
      end_date: '',
      status: 'active',
      instrument: instrumentHint || '',
      needs_rental: false,
      new_course: {
        subject_id: '',
        teacher_id: slot?.teacher_id || '',
        room_id: '',
        tariff_id: '',
        day_of_week: dayOfWeek,
        start_time: startTime,
        duration_minutes: duration,
        name: courseNameBits.join(' · '),
      },
    },
  };
}

export const ENROLLMENT_PREFILL_STORAGE_KEY = 'mamv.enrollmentPrefill';

let cachedPrefill = undefined;

export function hasEnrollmentPrefill() {
  try {
    return Boolean(sessionStorage.getItem(ENROLLMENT_PREFILL_STORAGE_KEY));
  } catch (_e) {
    return false;
  }
}

export function storeEnrollmentPrefill(payload) {
  cachedPrefill = undefined;
  try {
    sessionStorage.setItem(ENROLLMENT_PREFILL_STORAGE_KEY, JSON.stringify(payload));
  } catch (_e) { /* private mode */ }
}

export function consumeEnrollmentPrefill() {
  if (cachedPrefill !== undefined) return cachedPrefill;
  try {
    const raw = sessionStorage.getItem(ENROLLMENT_PREFILL_STORAGE_KEY);
    if (!raw) {
      cachedPrefill = null;
      return null;
    }
    sessionStorage.removeItem(ENROLLMENT_PREFILL_STORAGE_KEY);
    cachedPrefill = JSON.parse(raw);
    return cachedPrefill;
  } catch (_e) {
    cachedPrefill = null;
    return null;
  }
}
